import { useEffect, useRef } from 'react'
import { Link } from 'react-router-dom'
import type { LocaleInfo, Project } from '../api/types'
import type { LocaleLabel } from '../lib/locales'
import { SORT_LABELS, type SortKey } from '../lib/sort'
import { STATUS, type StatusKind } from '../lib/status'
import { useUi } from '../store/ui'

export function LeftRail({
  platform,
  projects,
  current,
  locales,
  labels,
  hidden,
  tags,
  statuses,
  showDeprecated,
  onShowDeprecatedChange,
  onNewProject,
  onDeleteProject,
}: {
  platform: string
  projects: Project[]
  current: Project | undefined
  locales: LocaleInfo[]
  labels: Record<string, LocaleLabel>
  hidden: string[]
  tags: string[]
  /** Statuses the loaded grid can actually answer for; see filterableStatuses. */
  statuses: StatusKind[]
  /** Findable/restorable: passed through as `include_deprecated` on the grid
   *  query, not a client-side filter — a deprecated key must be fetchable at
   *  all to be found and restored. */
  showDeprecated: boolean
  onShowDeprecatedChange: (value: boolean) => void
  onNewProject: () => void
  /** Never offered for the default project — see the caller. */
  onDeleteProject: (project: Project) => void
}) {
  const search = useUi((state) => state.search)
  const setSearch = useUi((state) => state.setSearch)
  const tag = useUi((state) => state.tag)
  const setTag = useUi((state) => state.setTag)
  const status = useUi((state) => state.status)
  const setStatus = useUi((state) => state.setStatus)
  const resetFilters = useUi((state) => state.resetFilters)
  const sort = useUi((state) => state.sort)
  const setSort = useUi((state) => state.setSort)
  const toggleLocale = useUi((state) => state.toggleLocale)
  const searchRef = useRef<HTMLInputElement>(null)

  // "/" focuses search from anywhere that is not already a text field.
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null
      const typing =
        target instanceof HTMLInputElement ||
        target instanceof HTMLTextAreaElement ||
        target?.isContentEditable
      if (event.key === '/' && !typing) {
        event.preventDefault()
        searchRef.current?.focus()
        searchRef.current?.select()
      }
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [])

  const defaultProject = projects.find((project) => project.is_default)
  const clientProjects = projects.filter((project) => !project.is_default)
  const filtersActive = Boolean(search || tag || status)

  return (
    <aside className="flex w-60 shrink-0 flex-col gap-3 overflow-y-auto border-r border-ink-200 bg-ink-50 px-2.5 py-3">
      <section>
        <h2 className="label mb-1.5">Project</h2>
        <ul className="space-y-0.5">
          {defaultProject && (
            <li>
              <ProjectLink
                platform={platform}
                slug="default"
                active={current?.id === defaultProject.id}
                title={defaultProject.name || 'Default'}
                subtitle="fallback wording for every client"
                badge="default"
              />
            </li>
          )}
          {clientProjects.map((project) => (
            <li key={project.id} className="group flex items-stretch gap-1">
              <div className="min-w-0 flex-1">
                <ProjectLink
                  platform={platform}
                  slug={project.slug ?? ''}
                  active={current?.id === project.id}
                  title={project.name}
                  subtitle={`org domain · ${project.slug}`}
                  badge="client"
                />
              </div>
              <button
                type="button"
                title={`Delete ${project.name}`}
                aria-label={`Delete ${project.name}`}
                className="shrink-0 rounded border border-transparent px-1 text-2xs text-ink-400 opacity-0 hover:border-rose-300 hover:bg-rose-50 hover:text-rose-800 focus:opacity-100 group-hover:opacity-100"
                onClick={() => onDeleteProject(project)}
              >
                Delete
              </button>
            </li>
          ))}
        </ul>
        <button className="btn mt-1.5 w-full justify-center" onClick={onNewProject}>
          + Client project
        </button>
      </section>

      <section>
        <h2 className="label mb-1.5">Locale columns</h2>
        <ul className="space-y-1">
          {locales.map((locale) => {
            const label = labels[locale.code]
            const shown = !hidden.includes(locale.code)
            return (
              <li key={locale.code}>
                <label className="flex cursor-pointer items-start gap-1.5 text-xs">
                  <input
                    type="checkbox"
                    className="mt-0.5"
                    checked={shown}
                    disabled={locale.is_source}
                    onChange={() => toggleLocale(platform, locale.code)}
                  />
                  <span className="min-w-0">
                    <span className="flex items-center gap-1">
                      <span className="font-mono text-2xs text-ink-700">{locale.code}</span>
                      {label?.variant && (
                        <span className="chip border-ink-300 bg-white text-ink-700">
                          {label.variant}
                        </span>
                      )}
                    </span>
                    <span className="block text-2xs text-ink-600">{label?.full}</span>
                  </span>
                </label>
              </li>
            )
          })}
        </ul>
      </section>

      <section>
        <label className="label mb-1.5 block" htmlFor="grid-sort">
          Sort
        </label>
        <select
          id="grid-sort"
          className="field text-xs"
          value={sort}
          onChange={(event) => setSort(event.target.value as SortKey)}
        >
          {(Object.keys(SORT_LABELS) as SortKey[]).map((key) => (
            <option key={key} value={key}>
              {SORT_LABELS[key]}
            </option>
          ))}
        </select>
      </section>

      <section>
        <div className="mb-1.5 flex items-center justify-between">
          <h2 className="label">Filters</h2>
          {filtersActive && (
            <button className="text-2xs text-blue-700 hover:underline" onClick={resetFilters}>
              clear
            </button>
          )}
        </div>
        <label className="sr-only" htmlFor="grid-search">
          Search keys and values
        </label>
        <input
          id="grid-search"
          ref={searchRef}
          className="field text-xs"
          type="search"
          placeholder="Search keys & values  ( / )"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Escape') setSearch('')
          }}
        />
        <label className="label mt-2 block" htmlFor="grid-tag">
          Tag
        </label>
        <select
          id="grid-tag"
          className="field mt-1 text-xs"
          value={tag ?? ''}
          onChange={(event) => setTag(event.target.value || null)}
        >
          <option value="">All tags</option>
          {tags.map((item) => (
            <option key={item} value={item}>
              {item}
            </option>
          ))}
        </select>
        <fieldset className="mt-2">
          <legend className="label">Status</legend>
          <div className="mt-1 flex flex-wrap gap-1">
            <FilterChip active={status === null} onClick={() => setStatus(null)} label="All" />
            {statuses.map((kind) => (
              <FilterChip
                key={kind}
                active={status === kind}
                onClick={() => setStatus(status === kind ? null : kind)}
                label={`${STATUS[kind].glyph} ${STATUS[kind].label}`}
                title={STATUS[kind].description}
                kind={kind}
              />
            ))}
          </div>
        </fieldset>
        <label className="mt-2 flex items-center gap-1.5 text-xs text-ink-700">
          <input
            type="checkbox"
            checked={showDeprecated}
            onChange={(event) => onShowDeprecatedChange(event.target.checked)}
          />
          Show deprecated keys
          <span className="text-2xs text-ink-500">(removed from exports, kept for restore)</span>
        </label>
      </section>
    </aside>
  )
}

function ProjectLink({
  platform,
  slug,
  active,
  title,
  subtitle,
  badge,
}: {
  platform: string
  slug: string
  active: boolean
  title: string
  subtitle: string
  badge: 'default' | 'client'
}) {
  return (
    <Link
      to={`/p/${platform}/${slug}`}
      aria-current={active ? 'page' : undefined}
      className={`block rounded border px-2 py-1.5 ${
        active
          ? 'border-blue-300 bg-white shadow-sm'
          : 'border-transparent hover:border-ink-200 hover:bg-white'
      }`}
    >
      <span className="flex items-center justify-between gap-1">
        <span className="truncate text-xs font-medium text-ink-900">{title}</span>
        <span
          className={`chip ${badge === 'default' ? 'border-blue-300 bg-blue-50 text-blue-800' : 'border-teal-300 bg-teal-50 text-teal-800'}`}
        >
          {badge}
        </span>
      </span>
      <span className="mt-0.5 block truncate font-mono text-2xs text-ink-600">{subtitle}</span>
    </Link>
  )
}

function FilterChip({
  active,
  onClick,
  label,
  title,
  kind,
}: {
  active: boolean
  onClick: () => void
  label: string
  title?: string
  kind?: StatusKind
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      aria-pressed={active}
      className={`chip border ${
        active
          ? 'border-blue-600 bg-blue-600 text-white'
          : kind
            ? STATUS[kind].className
            : 'border-ink-300 bg-white text-ink-700'
      }`}
    >
      {label}
    </button>
  )
}
